import { BaseTag, DashedTag, RiskTag } from '@praetorian-chariot/ui';
import React from 'react';

import { getStatusIcon } from '@/sections/jobs/components/cells';
import {
  AssetStatus,
  JobStatus,
  JobStatusLabel,
  RiskStatus,
  RiskStatusLabel,
} from '@/types';
import { getVulnerabilitySeverityTag } from '@/utils/entities/vulnerability.util';
import { VULNERABILITY_STATUS_DEFINITIONS } from '@/utils/mappings.util';

// Asset Status Chip
export const AssetStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getAssetStatusInfo = (status: string) => {
    // Normalize status to AssetStatus enum values
    const normalizedStatus = status?.toUpperCase();

    switch (normalizedStatus) {
      case 'A':
      case 'ACTIVE':
        return {
          label: 'Active',
          className: 'border-chariot-stroke-success bg-chariot-status-success',
        };
      case 'F':
      case 'FROZEN':
        return {
          label: 'Frozen',
          className: 'border-chariot-stroke-warning bg-chariot-status-warning',
        };
      case 'D':
      case 'DELETED':
        return {
          label: 'Deleted',
          className:
            'border-chariot-stroke-critical bg-chariot-status-critical',
        };
      case 'P':
      case 'PENDING':
        return {
          label: 'Pending',
          className: 'border-chariot-stroke-info bg-chariot-status-info',
        };
      case 'AL':
      case 'ACTIVELOW':
        return {
          label: 'Active Low',
          className: 'border-chariot-stroke-success bg-chariot-status-success',
        };
      case 'AP':
      case 'ACTIVEPASSIVE':
        return {
          label: 'Passive',
          className: 'border-chariot-stroke-success bg-chariot-status-success',
        };
      default:
        return {
          label: status || 'Unknown',
          className: 'border-chariot-stroke bg-chariot-background-secondary',
        };
    }
  };

  const { label, className } = getAssetStatusInfo(status);

  return (
    <BaseTag size="sm" className={className}>
      {label}
    </BaseTag>
  );
};

// Risk/Vulnerability Status Chip
export const RiskStatusChip: React.FC<{
  status: string;
  priority?: number;
  showSeverityInLabel?: boolean;
}> = ({ status, priority, showSeverityInLabel = true }) => {
  const getRiskStatusInfo = (status: string) => {
    const normalizedStatus = status?.toUpperCase();

    // Check if it's a composite status code (e.g., OC, OM, TH)
    if (
      normalizedStatus.length === 2 &&
      VULNERABILITY_STATUS_DEFINITIONS[normalizedStatus]
    ) {
      const { state, severity } =
        VULNERABILITY_STATUS_DEFINITIONS[normalizedStatus];
      // Only include severity in label if showSeverityInLabel is true
      const label = showSeverityInLabel ? `${state} (${severity})` : state;
      return { label, type: 'status' as const };
    }

    // Check single character status codes using RiskStatus enum
    const firstChar = normalizedStatus[0];
    if (Object.values(RiskStatus).includes(firstChar as RiskStatus)) {
      return {
        label: RiskStatusLabel[firstChar as RiskStatus],
        type: 'status' as const,
      };
    }

    // Fallback for full status names
    switch (normalizedStatus) {
      case 'OPEN':
      case 'OPENED':
        return {
          label: RiskStatusLabel[RiskStatus.Opened],
          type: 'status' as const,
        };
      case 'TRIAGED':
        return {
          label: RiskStatusLabel[RiskStatus.Triaged],
          type: 'status' as const,
        };
      case 'REMEDIATED':
        return {
          label: RiskStatusLabel[RiskStatus.Remediated],
          type: 'status' as const,
        };
      case 'ACCEPTED':
        return {
          label: RiskStatusLabel[RiskStatus.Accepted],
          type: 'status' as const,
        };
      case 'DELETED':
        return {
          label: RiskStatusLabel[RiskStatus.Deleted],
          type: 'status' as const,
        };
      default:
        return { label: status || 'Unknown', type: 'status' as const };
    }
  };

  const { label, type } = getRiskStatusInfo(status);

  // If we have a priority, show both severity and status
  if (priority !== undefined) {
    const severityTag = getVulnerabilitySeverityTag(priority);
    return (
      <div className="flex items-center gap-1">
        <RiskTag severity={severityTag} size="sm" />
        <DashedTag label={label} size="sm" />
      </div>
    );
  }

  // Otherwise just show status
  return <DashedTag label={label} size="sm" />;
};

// Job Status Icon (just icon, no chip)
export const JobStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const statusCode = status.split('#')[0] as JobStatus;
  const icon = getStatusIcon(statusCode, false); // Don't animate in tables
  const label = JobStatusLabel[statusCode] || status;

  return (
    <div className="flex items-center justify-center" title={label}>
      {icon}
    </div>
  );
};

// Generic Status Chip for other entities
export const StatusChip: React.FC<{
  status: string;
  type?: 'success' | 'warning' | 'error' | 'info' | 'default';
  label?: string;
}> = ({ status, type = 'default', label }) => {
  const getStatusClassName = (type: string) => {
    switch (type) {
      case 'success':
        return 'border-chariot-stroke-success bg-chariot-status-success';
      case 'warning':
        return 'border-chariot-stroke-warning bg-chariot-status-warning';
      case 'error':
        return 'border-chariot-stroke-critical bg-chariot-status-critical';
      case 'info':
        return 'border-chariot-stroke-info bg-chariot-status-info';
      default:
        return 'border-chariot-stroke bg-chariot-background-secondary';
    }
  };

  return (
    <BaseTag size="sm" className={getStatusClassName(type)}>
      {label || status}
    </BaseTag>
  );
};

// Smart Status Chip that auto-detects entity type
export const SmartStatusChip: React.FC<{
  status: string;
  priority?: number;
  entityType?: 'asset' | 'risk' | 'vulnerability' | 'job' | 'generic';
  showSeverityInLabel?: boolean;
}> = ({ status, priority, entityType, showSeverityInLabel }) => {
  // Auto-detect entity type if not provided
  if (!entityType) {
    const normalizedStatus = status?.toUpperCase();

    if (priority !== undefined) {
      entityType = 'vulnerability';
    } else if (
      status?.includes('#') &&
      ['JQ', 'JR', 'JF', 'JP', 'JRA', 'JQA'].includes(status.split('#')[0])
    ) {
      entityType = 'job';
    } else if (
      // Check composite status codes
      (normalizedStatus?.length === 2 &&
        VULNERABILITY_STATUS_DEFINITIONS[normalizedStatus]) ||
      // Check single character status codes
      Object.values(RiskStatus).includes(normalizedStatus?.[0] as RiskStatus) ||
      // Check full status names
      [
        'OPEN',
        'OPENED',
        'TRIAGED',
        'REMEDIATED',
        'ACCEPTED',
        'DELETED',
      ].includes(normalizedStatus)
    ) {
      entityType = 'risk';
    } else if (
      Object.values(AssetStatus).includes(normalizedStatus as AssetStatus) ||
      ['ACTIVE', 'FROZEN', 'PENDING', 'DELETED'].includes(normalizedStatus)
    ) {
      entityType = 'asset';
    } else {
      entityType = 'generic';
    }
  }

  switch (entityType) {
    case 'asset':
      return <AssetStatusChip status={status} />;
    case 'risk':
    case 'vulnerability':
      return (
        <RiskStatusChip
          status={status}
          priority={priority}
          showSeverityInLabel={showSeverityInLabel}
        />
      );
    case 'job':
      return <JobStatusIcon status={status} />;
    default:
      return <StatusChip status={status} />;
  }
};
